import { useCallback, useEffect, useRef, useState } from 'react';
import { samePhotos } from '@/hooks/selectEqual';
import { useLocalBytesUrl, useThumbnailUrl } from '@/hooks/usePhotoSources';
import { usePhotoState } from '@/hooks/usePhotoState';
import { useRowboat, useSelect } from '@/rowboat';
import type { PhotoRow } from '@/schema/estate';
import { deletePhoto, photosForItem, readMedia } from '@/services/photoService';
import type { PhotoState } from '@/services/uploadStatus';
import { usePhotoUploads } from './PhotoUploads';
import { PhotoViewer } from './PhotoViewer';

const HIGHLIGHT_MS = 1500;

// `checking` reads as waiting: until the queue is listed, a photo with a row but no keys
// is most likely queued on this device.
const STATE_TEXT: Record<PhotoState, string> = {
  checking: 'waiting to upload',
  waiting: 'waiting to upload',
  uploading: 'uploading',
  uploaded: 'uploaded',
  failed: 'upload failed',
  missing: 'not uploaded yet',
};

/** Ids that appear after mount, each kept for HIGHLIGHT_MS; the newest is scrolled into view. */
function useFreshTiles(ids: string[], enabled: boolean) {
  const key = ids.join('|');
  const seen = useRef<Set<string> | null>(null);
  const tiles = useRef(new Map<string, HTMLDivElement>());
  const timers = useRef<number[]>([]);
  const [fresh, setFresh] = useState<ReadonlySet<string>>(() => new Set());

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending) window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const current = key === '' ? [] : key.split('|');
    if (seen.current === null) {
      seen.current = new Set(current);
      return;
    }
    const known = seen.current;
    const added = current.filter((id) => !known.has(id));
    for (const id of added) known.add(id);
    if (!enabled || added.length === 0) return;
    setFresh((prev) => new Set([...prev, ...added]));
    tiles.current.get(added[added.length - 1])?.scrollIntoView?.({ block: 'nearest' });
    timers.current.push(
      window.setTimeout(
        () => setFresh((prev) => new Set([...prev].filter((id) => !added.includes(id)))),
        HIGHLIGHT_MS,
      ),
    );
  }, [key, enabled]);

  const tileRef = (id: string) => (el: HTMLDivElement | null) => {
    if (el) tiles.current.set(id, el);
    else tiles.current.delete(id);
  };
  return { fresh, tileRef };
}

/** Every photo on an item in sort order, each tile showing its image and upload state. */
export function PhotoGrid({
  itemId,
  previews,
  label,
  highlightNew = false,
  readOnly = false,
}: {
  itemId: string;
  /** Object URLs by photo id for shots taken while this item has been open. */
  previews: Record<string, string>;
  label: string;
  /** Scroll each photo added after mount into view and ring it briefly. */
  highlightNew?: boolean;
  /** Opens photos without Delete. */
  readOnly?: boolean;
}) {
  const graph = useRowboat();
  const photos = useSelect(() => photosForItem(graph, itemId), samePhotos);
  const { fresh, tileRef } = useFreshTiles(
    photos.map((p) => p.id),
    highlightNew,
  );
  const [viewing, setViewing] = useState<string>();
  const close = useCallback(() => setViewing(undefined), []);
  const open = photos.findIndex((p) => p.id === viewing);
  if (photos.length === 0) return null;
  return (
    <>
      <section
        aria-label={label}
        className="grid grid-cols-3 gap-2 sm:grid-cols-[repeat(auto-fill,minmax(6rem,1fr))]"
      >
        {photos.map((photo, i) => (
          <PhotoTile
            key={photo.id}
            photo={photo}
            n={i + 1}
            preview={previews[photo.id]}
            highlighted={fresh.has(photo.id)}
            tileRef={tileRef(photo.id)}
            onOpen={() => setViewing(photo.id)}
          />
        ))}
      </section>
      {open >= 0 && (
        <PhotoViewer
          photo={photos[open]}
          n={open + 1}
          preview={previews[photos[open].id]}
          onClose={close}
          onDelete={readOnly ? undefined : () => deletePhoto(graph, photos[open].id).then(close)}
        />
      )}
    </>
  );
}

function PhotoTile({
  photo,
  n,
  preview,
  highlighted,
  tileRef,
  onOpen,
}: {
  photo: PhotoRow;
  n: number;
  preview: string | undefined;
  highlighted: boolean;
  tileRef: (el: HTMLDivElement | null) => void;
  onOpen: () => void;
}) {
  const state = usePhotoState(photo);
  const { retry } = usePhotoUploads();
  const local = useLocalBytesUrl(photo.id, preview === undefined);
  const onDevice = preview ?? local.url;
  // Wait for the local answer: bytes on this device are never fetched from the server.
  const thumbHash = onDevice || !local.done ? undefined : readMedia(photo.thumbnail_key)?.hash;
  const downloaded = useThumbnailUrl(thumbHash);
  const src = onDevice ?? downloaded;
  return (
    <div
      ref={tileRef}
      data-highlighted={highlighted ? 'true' : undefined}
      className={`relative rounded-xl ${highlighted ? 'ring-2 ring-primary-500' : ''}`}
    >
      <button
        type="button"
        aria-label={`Photo ${n}, ${STATE_TEXT[state]}`}
        onClick={onOpen}
        className="block aspect-square w-full overflow-hidden rounded-xl bg-neutral-300"
      >
        {src && <img src={src} alt="" className="h-full w-full object-cover" />}
      </button>
      <StateBadge state={state} />
      {state === 'failed' && (
        <button
          type="button"
          aria-label={`Retry upload, photo ${n}`}
          onClick={retry}
          className="absolute bottom-1 left-1 rounded-lg bg-red-600 px-2 py-0.5 text-xs font-medium text-white"
        >
          Retry
        </button>
      )}
    </div>
  );
}

function StateBadge({ state }: { state: PhotoState }) {
  if (state === 'missing') return null;
  const look = {
    checking: 'bg-black/60',
    waiting: 'bg-black/60',
    uploading: 'bg-black/60',
    uploaded: 'bg-green-600',
    failed: 'bg-red-600',
  }[state];
  return (
    <span
      aria-hidden="true"
      className={`pointer-events-none absolute right-1 top-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-xs text-white ${look}`}
    >
      {state === 'uploading' ? (
        <span className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
      ) : (
        { checking: '◷', waiting: '◷', uploaded: '✓', failed: '!' }[state]
      )}
    </span>
  );
}
